import { applyAuthStateLoggedIn } from '../../core/auth-state';
import { qsa, on } from '../../core/dom';
import type { NavModuleInit } from '../../core/types';
import { socialErrorCopy } from './social-errors';

interface PopoverHandle {
  destroy(): void;
  isOpen(): boolean;
}

const HIDE_ON_ERROR = [
  '.fui-social-popover__title',
  '.fui-social-popover__buttons',
  '.fui-social-popover__separator',
];

function clearSocialError(): void {
  const root = document.querySelector<HTMLElement>('.fui-social-popover');
  if (!root) return;
  root.querySelector('.fui-social-popover__error')?.remove();
  HIDE_ON_ERROR.forEach((selector) => {
    const element = root.querySelector<HTMLElement>(selector);
    if (element) element.style.display = '';
  });
}

function showSocialError(code: string): void {
  const copy = socialErrorCopy(code);
  if (!copy) return;
  const root = document.querySelector<HTMLElement>('.fui-social-popover');
  if (!root) return;
  clearSocialError();
  const errorBox = document.createElement('div');
  errorBox.className = 'fui-social-popover__error';
  errorBox.setAttribute('role', 'alert');
  const title = document.createElement('h3');
  title.className = 'fui-social-popover__error-title';
  title.textContent = copy.info;
  errorBox.appendChild(title);
  if (copy.message) {
    const description = document.createElement('p');
    description.className = 'fui-social-popover__error-desc';
    description.textContent = copy.message;
    errorBox.appendChild(description);
  }
  const anchor = root.querySelector<HTMLElement>('.fui-social-popover__buttons');
  HIDE_ON_ERROR.forEach((selector) => {
    const element = root.querySelector<HTMLElement>(selector);
    if (element) element.style.display = 'none';
  });
  if (anchor) {
    anchor.insertAdjacentElement('afterend', errorBox);
  } else {
    root.appendChild(errorBox);
  }
}

const init: NavModuleInit = ({ state, bus, session }) => {
  const joinButtons = qsa<HTMLAnchorElement | HTMLButtonElement>(
    '[data-gn-trigger="join"], [data-testid="global-nav-join-button-desktop"]',
  );
  if (joinButtons.length === 0) return;

  let popover: PopoverHandle | null = null;

  const openPopover = async (anchor: HTMLElement) => {
    popover?.destroy();
    try {
      // @ts-ignore — vanilla subpath emitted by user-login build
      const mod = await import('@finder/user-login/lib/vanilla/social-popover');
      popover = mod.mountSocialPopover({
        anchor,
        loginLink: `${state.domain}/user-profile/sign-up`,
        sessionManager: session.getRawManager(),
        accountCreateFlow: 'web_nav_menu',
        heading: 'Finder free and exclusive deals',
        onSuccess: (method: 'google' | 'apple') => {
          clearSocialError();
          bus.emit('analytics:click', { label: `social-popover:success:${method}` });
          applyAuthStateLoggedIn(window.finderGlobalNav?.registry);
        },
        onFail: (err: Error) => {
          bus.emit('analytics:click', { label: `social-popover:fail:${err.message}` });
          showSocialError(err.message);
        },
        onClose: () => {
          popover = null;
        },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[global-nav-v2] social-popover failed to load, falling back to redirect', err);
      location.href = `${state.domain}/user-profile/sign-in`;
    }
  };

  const onJoinClick = (e: Event) => {
    e.preventDefault();
    void openPopover(e.currentTarget as HTMLElement);
  };

  const offJoins = joinButtons.map((b) => on(b, 'click', onJoinClick));

  return {
    destroy: () => {
      offJoins.forEach((off) => off());
      popover?.destroy();
    },
  };
};

export default init;
